import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

export const appTooltipContentClassName =
  "z-[250] max-w-xs rounded-md border-0 bg-zinc-900 px-3 py-2 text-xs font-medium leading-snug text-white shadow-[0_4px_14px_rgba(0,0,0,0.25)] animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1 dark:bg-zinc-800 dark:text-zinc-50";

export type AppTooltipProps = {
  /** Tooltip text or rich content */
  label: React.ReactNode;
  children: React.ReactElement;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  delayDuration?: number;
  disabled?: boolean;
  contentClassName?: string;
};

/**
 * Bootstrap-style tooltip — use anywhere inside `TooltipProvider` (root App already wraps it).
 *
 * @example
 * <AppTooltip label="Delete batch">
 *   <Button variant="ghost" size="icon"><Trash2 /></Button>
 * </AppTooltip>
 */
export function AppTooltip({
  label,
  children,
  side = "top",
  align = "center",
  sideOffset = 6,
  delayDuration = 200,
  disabled = false,
  contentClassName,
}: AppTooltipProps) {
  if (disabled || label == null || label === "") {
    return children;
  }

  const childDisabled =
    React.isValidElement(children) &&
    typeof children.props === "object" &&
    children.props !== null &&
    "disabled" in children.props &&
    Boolean((children.props as { disabled?: boolean }).disabled);

  const trigger = childDisabled ? <span className="inline-flex">{children}</span> : children;

  return (
    <Tooltip delayDuration={delayDuration}>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={cn(appTooltipContentClassName, contentClassName)}
      >
        {label}
        <TooltipPrimitive.Arrow className="fill-zinc-900 dark:fill-zinc-800" width={10} height={5} />
      </TooltipContent>
    </Tooltip>
  );
}
